import StarIcon from "@mui/icons-material/Star";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import CloseIcon from "@mui/icons-material/Close";
import FmdGoodIcon from "@mui/icons-material/FmdGood";
import { Box, IconButton, Modal, Stack, Typography, Card, Chip, CircularProgress } from "@mui/material";
import { useEffect, useState } from "react";
import moment from "moment";
import { Postrequestcall } from "../../request/Postrequest";
import { English } from "../../constant/EnglishFallback";
import { FETCH_USER_FAVORITE_JOB_POSTS } from "../../constant/Apipath";
import { useSelector } from "react-redux";
import Loader from "../../common/Loader";
import { getJobFavoriteKey, getExpiryLabel } from "./favoriteUtils";

const FavoriteJobsModal = ({ open, onClose, reloadKey, onFavoriteToggle, favoriteLoadingKeys }) => {
  const { multiLanguageData, loginData } = useSelector((state) => state?.main);
  const [favoriteJobs, setFavoriteJobs] = useState([]);
  const [loading, setLoading] = useState(false);

  const LanguageData = multiLanguageData?.JobPostings || {};
  const {
    TEXT_FAVORITE_JOBS = English.JobPostings.TEXT_FAVORITE_JOBS || "Favorite Jobs",
    TEXT_NO_JOB_POSTINGS_FOUND = English.JobPostings.TEXT_NO_JOB_POSTINGS_FOUND
  } = LanguageData || {};

  const fetchFavoriteJobs = async () => {
    if (!loginData?.userInformation?.UserId) return;
    setLoading(true);
    try {
      const payload = {
        PageNr: 1,
        NrOfRecPerPage: 20,
        FullSearch: "",
        UserId: Number(loginData.userInformation.UserId),
        IncludeRecordNr: true,
        TypeOfObjectReturned: "",
        FetchAllowedRecordsOnly: false,
        SearchList: [
          {
            userId: Number(loginData.userInformation.UserId)
          }
        ],
        SortList: [{}]
      };
      const res = await Postrequestcall(
        FETCH_USER_FAVORITE_JOB_POSTS,
        payload,
        loginData?.accessToken
      );
      const favoriteRecords = res?.data?.data || [];
      const flattenedJobs = favoriteRecords.flatMap((record) =>
        (record.jobList || []).map((job) => ({
          ...job,
          isExternal: job?.isExternal ?? record?.isExternal,
          favoriteRecordId: record.id,
          favoritesMeta: record
        }))
      );
      setFavoriteJobs(flattenedJobs);
    } catch (error) {
      console.error("fetchFavoriteJobs-> Error fetching favorites:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchFavoriteJobs();
    }
  }, [open, reloadKey]);

  const renderJob = (job) => {
    const jobKey = getJobFavoriteKey({ ...job, isExternal: job?.IsExternal });
    const expiryLabel = getExpiryLabel(job?.jobPostExpiryDate);
    const isFavoriteLoading = favoriteLoadingKeys?.[jobKey];
    const jobType = job?.jobPostTypes?.[0]?.typeName || job?.jobEmploymentType || "Job";
    const postedDateLabel = job?.jobPostedDate
      ? moment(job.jobPostedDate).format("MMM DD")
      : job?.dateCreated
        ? moment(job.dateCreated).format("MMM DD")
        : "";
    return (
      <Card
        key={jobKey}
        sx={{
          borderRadius: "12px",
          boxShadow: "0px 10px 30px rgba(0, 0, 0, 0.08)",
          border: "1px solid #e0e0e0",
          p: 2,
          backgroundColor: "white"
        }}>
        <Stack direction="row" spacing={2} alignItems="flex-start">
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: "12px",
              backgroundColor: "#eaf3ff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}>
            <BusinessCenterIcon sx={{ fontSize: 26, color: "#1a73e8" }} />
          </Box>
          <Stack spacing={0.4} flex={1}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Stack spacing={0.2}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {job?.jobTitle || job?.title || "Untitled"}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {job?.companyName || job?.company || ""}
                </Typography>
              </Stack>
              <Stack alignItems="flex-end" spacing={0.25}>
                <Chip
                  label={jobType}
                  size="small"
                  sx={{
                    borderRadius: 1,
                    bgcolor: "#f0f4ff",
                    color: "#1a1a1a",
                    fontWeight: 600,
                    fontSize: "0.7rem"
                  }}
                />
                <Typography variant="body2" color="textSecondary">
                  {postedDateLabel}
                </Typography>
              </Stack>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={1}>
              <FmdGoodIcon sx={{ fontSize: 16, color: "#666" }} />
              <Typography variant="body2" color="textSecondary">
                {job?.location || job?.locationName || ""}
              </Typography>
            </Stack>
            {Array.isArray(job?.trades) && job.trades.length > 0 && (
              <Stack direction="row" flexWrap="wrap" spacing={0.5} sx={{ mt: 0.5 }}>
                {job.trades.slice(0, 3).map((trade) => (
                  <Chip
                    key={trade?.tradeId || trade?.id || trade?.tradeName}
                    label={trade?.tradeName}
                    size="small"
                    sx={{
                      bgcolor: "#f0f2f5",
                      color: "#333",
                      fontWeight: 500,
                      borderRadius: 1
                    }}
                  />
                ))}
                {job.trades.length > 3 && (
                  <Chip
                    label={`+${job.trades.length - 3} more`}
                    size="small"
                    sx={{ bgcolor: "#f0f2f5", color: "#333", borderRadius: 1 }}
                  />
                )}
              </Stack>
            )}
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}> 
              {job?.isApplied && (
                <Chip
                  label="Applied"
                  size="small"
                  sx={{
                    bgcolor: "#e7f5ec",
                    color: "#1f7a3d",
                    fontWeight: 600,
                    borderRadius: 1
                  }}
                />
              )}
              {expiryLabel && (
                <Chip
                  label={expiryLabel}
                  size="small"
                  sx={{
                    bgcolor: "#ffe8e8",
                    color: "#c53030",
                    fontWeight: 600,
                    borderRadius: 1
                  }}
                />
              )}
            </Stack>
          </Stack>
          <Box>
            {isFavoriteLoading ? (
              <CircularProgress size={28} />
            ) : (
              <IconButton
                onClick={(event) => onFavoriteToggle?.(event, { ...job, isExternal: job?.IsExternal })}
                sx={{ color: "#f5c518" }}>
                <StarIcon />
              </IconButton>
            )}
          </Box>
        </Stack>
      </Card>
    );
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box className="user-modal-responsive" sx={{ maxHeight: "80vh", overflow: "auto" }}>
        <Stack spacing={1}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">{TEXT_FAVORITE_JOBS}</Typography>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Stack>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <Loader />
            </Box>
          ) : favoriteJobs.length > 0 ? (
            <Stack spacing={2}>{favoriteJobs.map(renderJob)}</Stack>
          ) : (
            <Box sx={{ py: 4, textAlign: "center" }}>
              <Typography variant="body2" color="textSecondary">
                {TEXT_NO_JOB_POSTINGS_FOUND}
              </Typography>
            </Box>
          )}
        </Stack>
      </Box>
    </Modal>
  );
};

export default FavoriteJobsModal;
